import { useId, useState } from 'react';
import type { JSX } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { RowMenu } from '../ui/row-menu';
import { PaneSection } from './PaneParts.js';
import type { TranslationKeys } from '../../i18n/types.js';
import { forkWords, isReserve, mainLine, type PlanBeatLike } from './beatPlan.js';
import type { Beat } from './StudioRail.js';
import { beatDisplayName } from './ownerPlace.js';
import { Button } from '../ui/button';
import { AddLink } from '../ui/add-link';
import { Pill } from '../ui/pill';
import { Field } from '../ui/field';
import { Input } from '../ui/input';
import { SelectField } from '../ui/field';

/**
 * «Куда дальше» in the beat overview (P04, Q-4): the beat's forks — «Если проиграли → Плен в логове» —
 * and the form of a new one. A fork leads to an existing beat or to a new reserve beat that the
 * server makes in the same step. Everything is echo-driven: nothing is shown until the server
 * answers (beat:outcomes-changed / beat:created). Mockup: gm-studio-quests-preview.html, `renderBeat()`
 * «Исходы», `outcomeForm()`.
 */

type Translate = (key: keyof TranslationKeys) => string;

const NEW_RESERVE = '__new_reserve__';

export interface BeatForksProps {
  readonly beat: Beat;
  /** Every beat of the plan (the targets of a fork, the names in the rows). */
  readonly beats: readonly Beat[];
  readonly t: Translate;
  readonly onOpenBeat: (beatId: string) => void;
  /** Exactly one of toBeatId / newReserve. */
  readonly onAdd: (fork: { readonly label: string; readonly toBeatId?: string; readonly newReserve?: { readonly label: string } }) => void;
  readonly onRemove: (outcomeId: string) => void;
  /** AU-8: forks the AI added and the GM has not seen yet — lit (`.ai-new`). */
  readonly litOutcomeIds?: ReadonlySet<string>;
}

export function BeatForks({ beat, beats, t, onOpenBeat, onAdd, onRemove, litOutcomeIds }: BeatForksProps): JSX.Element {
  const headingId = useId();
  const [adding, setAdding] = useState<boolean>(false);
  const [label, setLabel] = useState<string>('');
  const [target, setTarget] = useState<string>(NEW_RESERVE);
  const [reserveName, setReserveName] = useState<string>('');
  const fill = (key: keyof TranslationKeys, values: Record<string, string>): string =>
    Object.entries(values).reduce((text, [k, v]) => text.split(`{${k}}`).join(v), t(key));

  const outcomes = beat.outcomes ?? [];
  const nameOf = (id: string): string => {
    const found = beats.find((b) => b.id === id);
    return found === undefined ? '' : beatDisplayName(found, t);
  };
  const reserveTarget = (id: string): boolean => {
    const found = beats.find((b) => b.id === id);
    return found !== undefined && isReserve(found as PlanBeatLike);
  };

  const main = mainLine(beats);
  const options: Array<{ readonly value: string; readonly text: string }> = [
    ...main.filter((b) => b.id !== beat.id).map((b) => ({ value: b.id, text: fill('beat.next.optionMain', { n: String(main.indexOf(b) + 1), name: beatDisplayName(b, t) }) })),
    ...beats.filter((b) => isReserve(b as PlanBeatLike) && b.id !== beat.id).map((b) => ({ value: b.id, text: fill('beat.next.optionReserve', { name: beatDisplayName(b, t) }) })),
  ];

  const reset = (): void => {
    setAdding(false);
    setLabel('');
    setTarget(NEW_RESERVE);
    setReserveName('');
  };
  const submit = (): void => {
    const typed = label.trim();
    if (typed === '') return;
    if (target === NEW_RESERVE) {
      const named = reserveName.trim();
      onAdd({ label: typed, newReserve: { label: named !== '' ? named : fill('beat.reserve.railCaption', { label: typed }) } });
    } else {
      onAdd({ label: typed, toBeatId: target });
    }
    reset();
  };

  return (
    <PaneSection headingId={headingId} title={t('beat.next.title')} className="studio-beat-forks gap-1.5" testid="beat-forks">
      {outcomes.length === 0 ? (
        <p className="text-meta text-dim">{t('beat.next.noForks')}</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {outcomes.map((outcome) => {
            const reserve = reserveTarget(outcome.toBeatId);
            const outcomeLabel = fill('beat.next.outcome', { label: forkWords(outcome.label), beat: nameOf(outcome.toBeatId) });
            return (
              <li
                key={outcome.id}
                className={`flex h-7 min-w-0 max-w-full items-center rounded-md border bg-panel-2 pr-0.5 ${reserve ? 'border-reserve/45' : 'border-line'}${litOutcomeIds?.has(outcome.id) ? ' ring-1 ring-inset ring-ai/55 bg-ai/10' : ''}`}
                data-ai-new={litOutcomeIds?.has(outcome.id) ? 'true' : undefined}
                data-testid="beat-fork"
              >
                <button
                  type="button"
                  className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-l-md px-2 text-left text-body-s text-fg hover:bg-line/60"
                  title={outcomeLabel}
                  onClick={(): void => onOpenBeat(outcome.toBeatId)}
                >
                  <span className="min-w-0 truncate">
                    {outcomeLabel}
                  </span>
                  {reserve ? (
                    <Pill tone="reserve" className="flex-none">
                      {t('beat.reserve.pill')}
                    </Pill>
                  ) : null}
                </button>
                {/* П5: «Убрать развилку» is in the ⋯ of the fork, in words; not a deletion (the beats stay), so not red. */}
                <RowMenu
                  testid="beat-fork-menu"
                  items={[
                    { label: t('beat.next.openBeat'), icon: ArrowRight, onSelect: () => onOpenBeat(outcome.toBeatId) },
                    { label: t('beat.next.removeFork'), icon: X, testid: 'beat-fork-remove', onSelect: () => onRemove(outcome.id) },
                  ]}
                />
              </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <form
          aria-label={t('beat.next.formLabel')}
          className="flex flex-wrap items-end gap-2 rounded-md border border-line bg-panel-2 px-2.5 py-2"
          onSubmit={(e): void => {
            e.preventDefault();
            submit();
          }}
        >
          <Field label={t('beat.next.if')}>
            <Input
              autoFocus
              type="text"
              className="w-36"
              placeholder={t('beat.next.ifPlaceholder')}
              value={label}
              onChange={(e): void => setLabel(e.target.value)}
            />
          </Field>
          <Field label={t('beat.next.leadsTo')}>
            <SelectField className="w-56" value={target} onChange={(e): void => setTarget(e.target.value)}>
              <option value={NEW_RESERVE}>{t('beat.next.newReserve')}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </SelectField>
          </Field>
          {target === NEW_RESERVE ? (
            <Field label={t('beat.next.reserveName')}>
              <Input
                type="text"
                className="w-48"
                placeholder={t('beat.next.reservePlaceholder')}
                value={reserveName}
                onChange={(e): void => setReserveName(e.target.value)}
              />
            </Field>
          ) : null}
          <Button type="submit" size="sm" disabled={label.trim() === ''}>
            {t('beat.next.add')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reset}>
            {t('beat.next.cancel')}
          </Button>
        </form>
      ) : (
        <AddLink plus={false} onClick={(): void => setAdding(true)}>
          {t('beat.next.addFork')}
        </AddLink>
      )}
    </PaneSection>
  );
}

export default BeatForks;
